import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDomainStore, useNotificationPreferencesStore } from '@/hooks/useStores';
import FamilyCard from '@/components/family/FamilyCard';
import CreateFamilyCard from '@/components/family/CreateFamilyCard';
import FamiliesEmptyHero from '@/components/family/FamiliesEmptyHero';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import FamilyRenameCallout from '@/components/family/FamilyRenameCallout';
import Spinner from '@/components/shared/Spinner';
import PageTourIntro from '@/components/help/PageTourIntro';
import WelcomeBanner from '@/components/help/WelcomeBanner';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import { useScreenTour } from '@/hooks/useScreenTour';
import {
  FAMILIES_LIST_TOUR_STEPS,
  FAMILIES_LIST_TOUR_STORAGE_KEY,
  FAMILIES_LIST_WELCOME_STORAGE_KEY
} from '@/data/families-list-tour';
import '@/styles/families.css';

const FamiliesScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const domainStore = useDomainStore();
  const notificationPrefsStore = useNotificationPreferencesStore();
  const navigate = useNavigate();

  const allowedToCreate = domainStore.user?.allowedToCreateFamily ?? false;
  const families = domainStore.familiesMetadata;
  const isLoading = domainStore.isLoadingFamiliesMetaData;
  const userName = domainStore.user?.displayName ?? 'there';
  const userId = domainStore.user?.id;
  const [editNameFamilyId, setEditNameFamilyId] = useState<string | null>(null);

  const editNameFamily = editNameFamilyId ? domainStore.familiesMetadata.find((f) => f.id === editNameFamilyId) : null;

  const tourSteps =
    families.length === 0
      ? FAMILIES_LIST_TOUR_STEPS.filter(
          (s) =>
            s.target !== '[data-tour="families-grid"]' &&
            s.target !== '[data-tour="invite-hint"]' &&
            s.target !== '[data-tour="family-card-rename"]'
        )
      : FAMILIES_LIST_TOUR_STEPS;

  const familiesTour = useScreenTour({
    steps: FAMILIES_LIST_TOUR_STEPS,
    completedStorageKey: FAMILIES_LIST_TOUR_STORAGE_KEY,
    welcomeStorageKey: FAMILIES_LIST_WELCOME_STORAGE_KEY,
    resolveSteps: () => tourSteps
  });

  useEffect(() => {
    domainStore.setSelectedFamily(null);

    const loadFamilies = async () => {
      try {
        await domainStore.loadFamiliesMetadata();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load families:', error);
      }
    };

    void loadFamilies();
    void notificationPrefsStore.loadUnreadSummary();
  }, [domainStore, notificationPrefsStore]);

  const handleCreateFamilyClick = () => {
    navigate('/create-family');
  };

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!editNameFamily) return;
    domainStore.updateFamilyMetadata({
      id: editNameFamily.id,
      name: editNameFamily.name,
      displayName,
      alternativeName,
      memberCount: editNameFamily.memberCount,
      createdAt: editNameFamily.createdAt,
      updatedAt: editNameFamily.updatedAt,
      createdBy: editNameFamily.createdBy ?? null,
      createdByNickname: editNameFamily.createdByNickname ?? null,
      updatedBy: editNameFamily.updatedBy ?? null,
      updatedByNickname: editNameFamily.updatedByNickname ?? null,
      userRole: editNameFamily.userRole ?? null,
      canDeleteFamily: editNameFamily.canDeleteFamily ?? false
    });
  };

  return (
    <div className="families-page">
      <main className="families-page__main">
        {familiesTour.showWelcome && !isLoading && families.length > 0 ? (
          <WelcomeBanner name={userName} onStart={familiesTour.startTour} onDismiss={familiesTour.dismissWelcome} />
        ) : null}

        {!isLoading && families.length > 0 ? <FamilyRenameCallout userId={userId} /> : null}

        <header className="fl-header" data-tour="families-header">
          <PageTourIntro
            className="fl-header__tour-intro"
            onStartTour={familiesTour.startTour}
            heading={<h1 className="fl-header__title">{t('families.title')}</h1>}
          >
            <p className="fl-header__sub">
              {t('families.lede')} <ScreenHelpDot />
            </p>
          </PageTourIntro>
        </header>

        {isLoading ? (
          <div className="families-loading">
            <Spinner message={t('families.loading')} />
          </div>
        ) : families.length === 0 ? (
          <FamiliesEmptyHero allowed={allowedToCreate} onCreate={handleCreateFamilyClick} />
        ) : (
          <>
            <div className="families-grid" data-tour="families-grid">
              {families.map((family, index) => (
                <FamilyCard
                  key={family.id}
                  family={family}
                  onEditName={() => setEditNameFamilyId(family.id)}
                  renameTourAnchor={index === 0}
                />
              ))}
              <CreateFamilyCard allowed={allowedToCreate} onClick={handleCreateFamilyClick} />
            </div>
            <p className="invite-hint" data-tour="invite-hint">
              {t('families.invitationEmailHint')}
            </p>
          </>
        )}
      </main>

      <ScreenTourLayer tour={familiesTour} />

      {editNameFamily ? (
        <EditFamilyNameModal
          familyId={editNameFamily.id}
          currentDisplayName={editNameFamily.displayName || editNameFamily.name}
          canonicalName={editNameFamily.name}
          hasAlternative={!!editNameFamily.alternativeName}
          isOpen={!!editNameFamilyId}
          onClose={() => setEditNameFamilyId(null)}
          onSuccess={handleEditNameSuccess}
        />
      ) : null}
    </div>
  );
});

export default FamiliesScreen;
